import { CartAddEvent } from '@theme/events';
import { SLOTS, emptySelection, restoreSelection, countSelected, buildProperties, supportsPlaceNames, restorePlaceNames, hasCalendarPhotos } from './calendar-builder-state.js?v=20260929-named-images';
import { PRESETS, resolvePreset, chooseRandomPhoto } from './calendar-builder-presets.js?v=20260929-named-images';

const normalise = (value) => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase();
const designId = () => globalThis.crypto?.randomUUID?.() || `calendar-${Date.now()}-${Math.random().toString(36).slice(2)}`;
// Optional rendered place-name versions of a catalogue photo; https only.
const namedImages = (photo) => {
  const absolute = (value) => {
    if (typeof value !== 'string' || !value) return null;
    try {
      const url = new URL(value.startsWith('//') ? `https:${value}` : value, window.location.origin);
      return url.protocol === 'https:' ? url.href : null;
    } catch { return null; }
  };
  const namedImage = absolute(photo.namedImage);
  return { namedImage, namedThumbnail: namedImage ? absolute(photo.namedThumbnail) || namedImage : null };
};

class CalendarBuilder extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    this.selection = emptySelection();
    this.wanted = Array(14).fill(null);
    this.catalogue = [];
    this.active = 0;
    this.visibleLimit = 24;
    this.reviewing = false;
    this.busy = false;
    this.loading = false;
    this.complete = false;
    this.presetUndo = null;
    this.photoUndo = null;
    this.libraryVisible = false;
    this.reviewTarget = undefined;
    this.presetsOpen = false;
    this.showPlaceNames = false;
    this.design = designId();
    this.storageKey = `cp-calendar:v1:${this.dataset.productId || 'preview'}:${this.dataset.year}:${this.dataset.size}`;
    this.$ = (name) => this.querySelector(`[data-${name}]`);
    this.recoveryLine = new URL(window.location.href).searchParams.get('calendar_line') || '';
    this.recoveryQuantity = null;
    this.$('recovery-notice').hidden = !this.recoveryLine;
    try {
      const saved = JSON.parse(localStorage.getItem(this.storageKey));
      if (saved?.version === 1 && Array.isArray(saved.ids) && saved.ids.length === 14) {
        this.wanted = saved.ids;
        this.showPlaceNames = restorePlaceNames(saved);
        this.design = /^[\w-]{1,100}$/.test(saved.designId) ? saved.designId : this.design;
        if (saved.recoveryLine === this.recoveryLine && Number.isInteger(saved.recoveryQuantity) && saved.recoveryQuantity > 0) this.recoveryQuantity = saved.recoveryQuantity;
      }
    } catch { this.announce('Unable to save on this device. Keep this page open.'); }
    const options = { signal: this.controller.signal };
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) { this.busy = false; this.removeAttribute('aria-busy'); this.render(); }
    }, options);
    this.addEventListener('click', (event) => this.onClick(event), options);
    this.$('search').addEventListener('input', (event) => {
      // A search always covers the whole collection, so drop any location filter.
      if (event.target.value.trim()) this.$('location').value = '';
      this.visibleLimit = 24; this.renderCatalogue();
    }, options);
    this.$('search').addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' || event.isComposing || event.keyCode === 229) return;
      event.preventDefault();
      event.currentTarget.blur();
    }, options);
    this.$('place-names').addEventListener('change', (event) => this.setPlaceNames(event.target.checked), options);
    this.$('location').addEventListener('change', (event) => {
      if (event.target.value) this.$('search').value = '';
      this.visibleLimit = 24; this.renderCatalogue();
    }, options);
    this.$('preview-empty').addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      this.openLibrary();
    }, options);
    this.$('preview-dialog').addEventListener('close', () => {
      this.updateMobileDock();
      (this.previewTrigger?.isConnected ? this.previewTrigger : this.$('enlarge-preview')).focus({ preventScroll: true });
    }, options);
    this.$('page-jump').addEventListener('change', (event) => event.target.value === 'review' ? this.showReview() : this.goTo(Number(event.target.value), { keepLibrary: true }), options);
    // "Or start from a ready-made calendar" near the top opens the curated calendars and goes to them.
    this.querySelector('[data-open-presets]')?.addEventListener('click', (event) => {
      event.preventDefault();
      if (!this.presetsOpen) this.setPresetsOpen(true);
      this.$('routes').scrollIntoView({ block: 'start', behavior: this.scrollBehavior() });
    }, options);
    this.buildSlots();
    this.$('builder-app').hidden = false;
    this.$('startup').hidden = true;
    this.render();
    if (typeof IntersectionObserver === 'function') {
      this.libraryObserver?.disconnect();
      this.libraryObserver = new IntersectionObserver(([entry]) => { this.libraryVisible = entry.isIntersecting; this.updateMobileDock(); }, { rootMargin: '-80px 0px -100px 0px' });
      this.libraryObserver.observe(this.querySelector('.cp-calendar__workspace'));
    }
    this.loadCatalogue();
  }

  disconnectedCallback() {
    clearTimeout(this.toastTimer);
    this.controller?.abort(); this.libraryObserver?.disconnect();
    this.presetAnimation?.cancel(); this.reviewAnimation?.cancel();
  }

  async fetchJSON(url, options = {}) {
    const request = new AbortController();
    const lifecycle = this.controller.signal;
    const abort = () => request.abort();
    lifecycle.addEventListener('abort', abort, { once: true });
    const timer = setTimeout(abort, 20000);
    try {
      const response = await fetch(url, { ...options, signal: request.signal, headers: { Accept: 'application/json', ...options.headers } });
      const data = await response.json();
      if (!response.ok) throw new Error(typeof data.description === 'string' ? data.description : 'The request could not be completed.');
      return data;
    } finally {
      clearTimeout(timer);
      lifecycle.removeEventListener('abort', abort);
    }
  }

  async loadCatalogue() {
    if (this.loading) return;
    this.loading = true;
    const lifecycle = this.controller.signal;
    this.complete = false;
    this.$('retry').hidden = true;
    this.$('empty').hidden = true;
    this.$('catalogue-status').textContent = 'Loading the full photograph collection…';
    const seen = new Set();
    const products = new Map();
    try {
      const first = new URL(this.dataset.catalogueUrl, window.location.origin);
      if (!first.pathname.includes('/collections/')) throw new Error('The photograph collection is not connected yet.');
      let next = first.href;
      while (next) {
        const url = new URL(next, window.location.origin);
        // Fetch the catalogue version containing fulfilment SKUs and optional label wording.
        url.searchParams.set('calendar_schema', '5');
        if (url.origin !== first.origin || url.pathname !== first.pathname || seen.has(url.href) || seen.size >= 250) throw new Error('The full collection could not be loaded.');
        seen.add(url.href);
        // This is an alternate Liquid view. Accept: application/json bypasses it
        // and Shopify returns its ordinary collection JSON instead.
        const data = await this.fetchJSON(url.href, { headers: { Accept: 'text/html' } });
        if (lifecycle.aborted || lifecycle !== this.controller.signal) return;
        if (!Array.isArray(data.products) || !Object.hasOwn(data, 'next')) throw new Error('The photograph collection is not ready.');
        for (const photo of data.products) {
          if (!/^\d+$/.test(String(photo.id)) || typeof photo.title !== 'string' || !photo.image) continue;
          const image = new URL(photo.image.startsWith('//') ? `https:${photo.image}` : photo.image, window.location.origin);
          const thumbSource = photo.thumbnail || photo.image;
          const thumbnail = new URL(thumbSource.startsWith('//') ? `https:${thumbSource}` : thumbSource, window.location.origin);
          if (image.protocol !== 'https:' || thumbnail.protocol !== 'https:') continue;
          const named = namedImages(photo);
          products.set(String(photo.id), { ...photo, id: String(photo.id), image: image.href, thumbnail: thumbnail.href, ...named, locations: Array.isArray(photo.locations) ? photo.locations : [] });
        }
        this.catalogue = [...products.values()];
        this.selection = restoreSelection(this.wanted, this.catalogue);
        this.renderLocations();
        this.render();
        next = data.next;
        this.$('catalogue-status').textContent = next ? `${this.catalogue.length} photographs loaded. Loading the rest…` : '';
      }
      if (!this.catalogue.length) throw new Error('There are no photographs in this collection yet.');
      const removed = this.wanted.filter(Boolean).length - countSelected(this.selection);
      this.wanted = this.selection.map((photo) => photo?.id || null);
      this.complete = true;
      this.renderPresets();
      this.save();
      if (removed > 0) this.announce('Some saved photographs are no longer available. Please choose replacements for the empty pages.');
    } catch (error) {
      if (lifecycle.aborted || lifecycle !== this.controller.signal) return;
      this.complete = false;
      this.$('catalogue-status').textContent = `${error.name === 'AbortError' ? 'Loading photographs timed out.' : error.message} You can keep choosing from the photographs shown. Load the full collection before ordering.`;
      this.$('retry').hidden = false;
      this.$('presets-loading').textContent = 'Load the full photograph collection to explore the curated calendars.';
    } finally {
      if (!lifecycle.aborted && lifecycle === this.controller.signal) { this.loading = false; this.render(); }
    }
  }

  save() {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify({ version: 1, ids: this.wanted, designId: this.design, showPlaceNames: this.showPlaceNames === true,
        ...(this.recoveryLine && this.recoveryQuantity ? { recoveryLine: this.recoveryLine, recoveryQuantity: this.recoveryQuantity } : {}),
      }));
    } catch { this.announce('Unable to save on this device. Keep this page open.'); }
  }

  setPlaceNames(enabled) {
    if (this.busy) return;
    this.showPlaceNames = Boolean(enabled);
    this.design = designId();
    this.save(); this.render();
    this.announcePendingPlaceNames();
  }

  // Pages whose photo has no rendered place-name version yet; the cart blocks them.
  announcePendingPlaceNames(prefix = '') {
    if (!this.showPlaceNames) return false;
    const pending = SLOTS.filter((slot, index) => this.selection[index] && !supportsPlaceNames(this.selection[index])).map((slot) => slot.label);
    if (!pending.length) return false;
    this.announce(`${prefix}Place names aren’t available yet for the photo on ${pending.join(', ')}. Choose another photo for ${pending.length === 1 ? 'that page' : 'those pages'}, or turn place names off.`);
    return true;
  }

  announce(message) { this.$('message').textContent = message; }

  // Brief visual confirmation; screen readers already hear the announce() message.
  showToast(text) {
    const toast = this.$('toast');
    if (!toast) return;
    clearTimeout(this.toastTimer);
    this.$('toast-text').textContent = text;
    toast.hidden = false;
    toast.classList.remove('is-visible');
    void toast.offsetWidth;
    toast.classList.add('is-visible');
    this.toastTimer = setTimeout(() => { toast.classList.remove('is-visible'); this.toastTimer = setTimeout(() => { toast.hidden = true; }, 250); }, 2200);
  }

  buildSlots() {
    this.$('slots').replaceChildren(...SLOTS.map((slot, index) => {
      const li = document.createElement('li');
      const button = document.createElement('button');
      button.type = 'button'; button.className = 'cp-calendar__slot'; button.dataset.slot = index;
      const mark = document.createElement('span'); mark.className = 'cp-calendar__slot-mark'; mark.setAttribute('aria-hidden', 'true');
      const label = document.createElement('span'); label.textContent = slot.label;
      button.append(mark, label); li.append(button); return li;
    }));
  }

  onClick(event) {
    if (this.busy) return;
    if (event.target.closest('[data-preview-empty]')) { this.openLibrary(); return; }
    const button = event.target.closest('button');
    if (!button || button.disabled) return;
    if (button.hasAttribute('data-added-another')) { this.startAnother(false); return; }
    if (button.hasAttribute('data-added-fresh')) { this.startAnother(true); return; }
    if (button.hasAttribute('data-slot')) this.goTo(Number(button.dataset.slot));
    if (button.hasAttribute('data-choose-photo')) {
      const photo = this.catalogue.find((item) => item.id === button.dataset.choosePhoto);
      if (!photo) return;
      if (this.showPlaceNames && !supportsPlaceNames(photo)) {
        this.announce(`${photo.title} doesn’t have a place-name version yet. Choose another photo, or turn place names off.`);
        return;
      }
      if (this.selection[this.active]?.id === photo.id) return;
      const page = this.active;
      const filledEmptyPage = !this.selection[page];
      this.rememberPhotoChange();
      this.selection[page] = photo; this.wanted[page] = photo.id;
      this.clearPresetUndo();
      // Filling an empty page moves straight on to the next empty one and leaves the photo list where it is.
      // Replacing a photo stays on that page so the change can be checked in the preview.
      const nextEmpty = filledEmptyPage ? this.nextEmptyPage(page) : -1;
      if (nextEmpty !== -1) this.active = nextEmpty;
      this.design = designId(); this.save(); this.render();
      const label = SLOTS[page].label;
      if (nextEmpty !== -1) {
        this.announce(`${photo.title} added to ${label}. Now choosing ${SLOTS[nextEmpty].label}.`);
        this.showToast(`${label} added · now ${SLOTS[nextEmpty].label}`);
        this.fadeIn(this.$('library-heading'));
        this.$('catalogue').querySelector(`[data-choose-photo="${photo.id}"]`)?.focus({ preventScroll: true });
      } else if (filledEmptyPage) {
        // The last empty page: every page is chosen, so show the whole year.
        this.showToast('All 14 pages chosen');
        this.showReview();
        this.announce(`${photo.title} added to ${label}. All 14 pages chosen. Check your calendar, then add it to your basket.`);
      } else {
        this.announce(`${photo.title} selected for ${label}.`);
        this.showToast(`${label} updated`);
        this.$('catalogue').querySelector(`[data-choose-photo="${photo.id}"]`)?.focus({ preventScroll: true });
        this.showPreview();
      }
    }
    if (button.hasAttribute('data-remove')) {
      this.rememberPhotoChange();
      this.selection[this.active] = null; this.wanted[this.active] = null;
      this.clearPresetUndo();
      this.design = designId(); this.save(); this.render();
      this.announce(`${SLOTS[this.active].label} photo removed.`);
      this.$('search').focus({ preventScroll: true });
    }
    if (button.hasAttribute('data-browse-prev')) this.goTo(this.active - 1, { keepLibrary: true });
    if (button.hasAttribute('data-browse-next')) this.active === 13 ? this.showReview() : this.goTo(this.active + 1, { keepLibrary: true });
    // Wider screens: move on without scrolling, so the photo list stays where it is.
    if (button.hasAttribute('data-next-page')) this.active === 13 ? this.showReview() : this.goTo(this.active + 1, { stay: true });
    if (button.hasAttribute('data-route-presets')) this.setPresetsOpen(!this.presetsOpen);
    if (button.hasAttribute('data-route-own')) this.startOwn();
    if (button.hasAttribute('data-review')) this.showReview();
    if (button.hasAttribute('data-close-review')) { this.reviewing = false; this.render(); this.$('current-label').scrollIntoView({ block: 'center', behavior: 'instant' }); }
    if (button.hasAttribute('data-clear-filters')) { this.$('search').value = ''; this.$('location').value = ''; this.visibleLimit = 24; this.renderCatalogue(); this.$('search').focus(); }
    if (button.hasAttribute('data-show-more')) {
      const firstNew = this.visibleLimit; this.visibleLimit += 24; this.renderCatalogue();
      this.$('catalogue').children[firstNew]?.focus();
    }
    if (button.hasAttribute('data-retry')) this.loadCatalogue();
    if (button.hasAttribute('data-random-photo')) this.randomPhoto();
    if (button.hasAttribute('data-apply-preset')) this.applyPreset(button.dataset.applyPreset);
    if (button.hasAttribute('data-presets-more')) this.setPresetsShowingAll(!this.presetsShowingAll);
    if (button.hasAttribute('data-undo-preset') || button.hasAttribute('data-undo-preset-review')) this.undoPreset();
    if (button.hasAttribute('data-undo-photo') || button.hasAttribute('data-undo-photo-mobile')) this.undoPhoto();
    if (button.hasAttribute('data-enlarge-preview')) this.enlargePreview(button);
    if (button.hasAttribute('data-close-preview')) this.$('preview-dialog').close();
    if (button.hasAttribute('data-purchase')) this.reviewing ? this.addToCart() : this.showReview();
  }

  openLibrary() {
    const heading = this.$('library-heading');
    const search = this.$('search');
    if (globalThis.matchMedia?.('(max-width: 640px)').matches) {
      heading.scrollIntoView({ block: 'start', behavior: this.scrollBehavior() });
      heading.focus({ preventScroll: true });
    } else {
      search.scrollIntoView({ block: 'nearest', behavior: this.scrollBehavior() });
      search.focus({ preventScroll: true });
    }
    this.announce(`Choose a photograph for ${SLOTS[this.active].label} from the list.`);
  }

  scrollBehavior() {
    return globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth';
  }

  // Scroll the calendar preview into view when it is off screen, so a new choice can be checked straight away.
  showPreview() {
    const stage = this.$('mockup');
    const rect = stage.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const visible = rect.top >= 0 && rect.bottom <= viewportHeight;
    if (visible) return;
    stage.scrollIntoView({ block: rect.height > viewportHeight - 120 ? 'start' : 'center', behavior: this.scrollBehavior() });
  }

  nextEmptyPage(from) {
    for (let step = 1; step < SLOTS.length; step += 1) {
      const index = (from + step) % SLOTS.length;
      if (!this.selection[index]) return index;
    }
    return -1;
  }

  /** "Choose all 14 myself": close the ready-made calendars and go to the photos for the next empty page. */
  startOwn() {
    const go = () => {
      if (countSelected(this.selection) === 14) { this.showReview(); return; }
      if (this.selection[this.active]) this.active = this.nextEmptyPage(this.active);
      this.reviewing = false; this.render();
      this.openLibrary();
    };
    if (this.presetsOpen) this.setPresetsOpen(false, go); else go();
  }

  goTo(index, { keepLibrary = false, stay = false } = {}) {
    if (!Number.isInteger(index) || index < 0 || index > 13) return;
    const wasReviewing = this.reviewing;
    this.active = index; this.reviewing = false; this.render();
    if (stay) {
      this.fadeIn(this.$('library-heading'));
    } else if (keepLibrary) {
      this.$('library-heading').focus({ preventScroll: true });
      this.$('library-heading').scrollIntoView({ block: 'start', behavior: globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
    } else {
      const slot = this.querySelector(`[data-slot="${index}"]`);
      slot?.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'instant' });
      slot?.focus({ preventScroll: true });
      if (wasReviewing) this.$('current-label').scrollIntoView({ block: 'center', behavior: 'instant' });
    }
    this.announce(`Editing ${SLOTS[index].label}. ${this.selection[index]?.title || 'Choose a photograph.'}`);
  }

  render() {
    const count = countSelected(this.selection);
    this.$('slots').querySelectorAll('[data-slot]').forEach((button, index) => {
      const selected = this.selection[index];
      button.classList.toggle('is-complete', Boolean(selected));
      button.setAttribute('aria-label', `${SLOTS[index].label}: ${selected?.title || 'no photograph selected'}`);
      if (index === this.active) button.setAttribute('aria-current', 'step'); else button.removeAttribute('aria-current');
      button.firstElementChild.textContent = selected ? '✓' : String(index + 1).padStart(2, '0');
    });
    this.$('progress').textContent = `${count} of 14 photographs chosen`;
    this.$('progress-bar').value = count;
    const placeOption = this.$('place-names');
    if (placeOption) { placeOption.checked = this.showPlaceNames === true; placeOption.disabled = this.busy; }
    this.renderRoutes(count);
    this.$('current-label').textContent = SLOTS[this.active].label;
    this.$('selecting-label').textContent = SLOTS[this.active].label;
    this.$('step').textContent = `${String(this.active + 1).padStart(2, '0')} / 14`;
    this.$('remove').disabled = !this.selection[this.active];
    this.$('random-photo').disabled = !this.complete || this.busy || !this.catalogue?.length;
    this.$('chosen-title').textContent = this.selection[this.active]?.title || '';
    this.$('chosen-title').hidden = !this.selection[this.active];
    this.$('remove').textContent = `Clear ${this.active === 0 || this.active === 13 ? 'cover' : SLOTS[this.active].label}`;
    this.$('remove').setAttribute('aria-label', `Clear ${SLOTS[this.active].label} selection`);
    this.renderEditingControls(count);
    this.renderPreview();
    this.renderCatalogue();
    if (this.reviewing) this.renderReview();
    this.setPanelVisible(this.$('review-panel'), this.reviewing);
    this.updateMobileDock();
    const configured = Boolean(this.dataset.variantId) && /^(20\d{2})$/.test(this.dataset.year);
    this.$('purchase').disabled = this.busy || count !== 14 || !this.complete || (this.reviewing && !configured);
    this.$('purchase').textContent = this.busy ? (this.recoveryLine ? 'Updating your calendar…' : 'Adding your calendar…') : count !== 14 ? `Choose ${14 - count} more photograph${count === 13 ? '' : 's'}` : !this.reviewing ? 'Review your calendar →' : !configured ? 'Not available to order yet' : this.recoveryLine ? 'Update calendar in basket' : 'Add calendar to basket';
    this.$('purchase-hint').textContent = count !== 14 ? 'Fill every page to continue.' : !this.complete ? 'Waiting for the full photo collection…' : !configured ? 'Ordering is not available yet.' : this.reviewing ? 'Ready to order.' : 'All 14 chosen. Ready to review.';
  }

  renderEditingControls(count) {
    const photo = this.selection[this.active];
    this.$('undo-photo').hidden = !this.photoUndo;
    this.$('undo-photo').disabled = this.busy;
    this.$('undo-photo-mobile').hidden = !this.photoUndo;
    this.$('undo-photo-mobile').disabled = this.busy;
    this.$('undo-preset-review').hidden = !this.presetUndo;
    this.$('undo-preset-review').disabled = this.busy;
    this.$('page-jump').replaceChildren(...SLOTS.map((slot, index) => new Option(`${slot.label}${this.selection[index] ? ' ✓' : ' · empty'}`, String(index))), new Option('Review all 14 pages', 'review'));
    this.$('page-jump').value = String(this.active);
    this.$('browse-prev').disabled = this.active === 0 || this.busy;
    this.$('browse-next').disabled = this.busy;
    this.$('browse-next').textContent = this.active === 13 ? 'Review →' : `${SLOTS[this.active + 1].label} →`;
    this.$('next-page').disabled = this.busy;
    this.$('next-page').textContent = this.active === 13 ? 'Review your calendar →' : `Next: ${SLOTS[this.active + 1].label} →`;
    this.$('dock-label').textContent = `${SLOTS[this.active].label} · ${count}/14 chosen`;
    this.$('dock-title').textContent = photo?.title || 'Choose a photograph below';
    this.$('dock-image').hidden = !photo;
    if (photo && this.$('dock-image').src !== photo.thumbnail) this.$('dock-image').src = photo.thumbnail;
    this.$('dock-preview').setAttribute('aria-label', `Preview ${SLOTS[this.active].label}: ${photo?.title || 'no photo selected'}`);
    const instruction = document.createElement('strong'); instruction.textContent = 'Click a month to edit it.';
    this.$('review-summary').replaceChildren(instruction, count === 14 ? '' : ` ${14 - count} page${14 - count === 1 ? ' still needs' : 's still need'} a photo.`);
  }

  /** The two ways in describe where the customer is: fresh start, part way, or finished. */
  renderRoutes(count) {
    const next = this.selection[this.active] ? this.nextEmptyPage(this.active) : this.active;
    this.$('route-own-title').textContent = count === 0 ? 'Choose all 14 myself' : count === 14 ? 'Review your calendar' : 'Carry on choosing';
    this.$('route-own-text').textContent = count === 0 ? 'Pick a photo for each page, starting with the front cover.'
      : count === 14 ? 'All 14 pages chosen. Change any page from the review.'
      : `${14 - count} page${count === 13 ? '' : 's'} left. Next up: ${SLOTS[next]?.label || SLOTS[this.active].label}.`;
    this.$('route-presets-text').textContent = count === 0
      ? `${PRESETS.length} Cornwall themes with all 14 pages filled. Change any page after.`
      : 'Replaces your current photos. You can undo it.';
    this.$('route-presets').setAttribute('aria-expanded', String(this.presetsOpen));
    this.$('route-own').disabled = this.busy;
  }

  updateMobileDock() {
    this.$('mobile-dock').hidden = !this.libraryVisible || this.reviewing || this.$('preview-dialog').open;
  }

  rememberPhotoChange() {
    this.photoUndo = { wanted: [...this.wanted], design: this.design, active: this.active };
  }

  undoPhoto() {
    if (!this.photoUndo || this.busy) return;
    const previous = this.photoUndo;
    this.wanted = [...previous.wanted]; this.selection = restoreSelection(this.wanted, this.catalogue);
    this.design = previous.design; this.active = previous.active;
    this.photoUndo = null; this.clearPresetUndo(); this.save(); this.render();
    this.announce('Photo change undone.');
    const focusTarget = this.libraryVisible && globalThis.matchMedia?.('(max-width: 640px)').matches ? 'library-heading' : 'search';
    this.$(focusTarget).focus({ preventScroll: true });
  }

  renderPreview() {
    const changedPage = this.previewPage !== this.active;
    this.renderPreviewInto(this.$('mockup'), this.active, this.selection[this.active]);
    if (changedPage && this.active > 0 && this.active < 13) this.fadeIn(this.$('date-sheet'));
    this.previewPage = this.active;
  }

  renderPreviewInto(mockup, index, photo) {
    const img = mockup.querySelector('.cp-calendar__photo-sheet img');
    const loading = mockup.querySelector('.cp-calendar__photo-loading');
    // With place names on, pages show the rendered image that has the label printed in.
    const source = photo && this.showPlaceNames && photo.namedImage ? photo.namedImage : photo?.image;
    mockup.querySelector('.cp-calendar__empty-photo').hidden = Boolean(photo);
    img.onload = null;
    img.onerror = null;
    if (photo) {
      img.alt = `${SLOTS[index].label} calendar preview: ${photo.title}`;
      const reveal = () => {
        if (img.src !== source || !img.complete || !img.naturalWidth) return;
        img.hidden = false;
        if (loading) loading.hidden = true;
      };
      img.hidden = true;
      if (loading) { loading.hidden = false; loading.textContent = 'Loading photo…'; }
      img.onload = () => { reveal(); if (!img.hidden) this.fadeIn(img); };
      img.onerror = () => {
        if (img.src !== source) return;
        img.hidden = true;
        if (loading) { loading.hidden = false; loading.textContent = 'Photo couldn’t load. Try another page, then return.'; }
      };
      if (img.src !== source) img.src = source;
      reveal();
    } else {
      img.hidden = true;
      img.removeAttribute('src'); img.alt = '';
      if (loading) loading.hidden = true;
    }
    const isCover = index === 0 || index === 13;
    mockup.classList.toggle('is-cover', isCover);
    mockup.classList.toggle('is-month', !isCover);
    mockup.querySelector('.cp-calendar__date-sheet').hidden = isCover;
    if (isCover) return;
    const month = index;
    mockup.querySelector('.cp-calendar__date-heading strong').textContent = SLOTS[month].label;
    const year = /^20\d{2}$/.test(this.dataset.year) ? Number(this.dataset.year) : 2027;
    const start = (new Date(Date.UTC(year, month - 1, 1)).getUTCDay() + 6) % 7;
    const days = new Date(Date.UTC(year, month, 0)).getUTCDate();
    const weeks = Math.ceil((start + days) / 7);
    const grid = mockup.querySelector('.cp-calendar__date-grid');
    grid.style.setProperty('--calendar-weeks', weeks);
    grid.replaceChildren(...['M', 'T', 'W', 'T', 'F', 'S', 'S', ...Array(start).fill(''), ...Array.from({ length: days }, (_, i) => i + 1), ...Array(weeks * 7 - start - days).fill('')].map((day, i) => {
      const cell = document.createElement('span'); cell.textContent = day; if (i < 7) cell.className = 'weekday'; return cell;
    }));
  }

  createPreview(index, photo, decorative = false) {
    const preview = this.$('mockup').cloneNode(true);
    this.renderPreviewInto(preview, index, photo);
    // Clones must not become additional controller targets or duplicate live IDs.
    for (const element of [preview, ...preview.querySelectorAll('*')]) {
      for (const attribute of [...element.attributes]) {
        if (attribute.name.startsWith('data-') || attribute.name === 'id') element.removeAttribute(attribute.name);
      }
      // Only the live preview's empty state is a control.
      if (element.classList.contains('cp-calendar__empty-photo')) {
        for (const name of ['role', 'tabindex', 'aria-label']) element.removeAttribute(name);
      }
    }
    if (decorative) preview.setAttribute('aria-hidden', 'true');
    return preview;
  }

  enlargePreview(trigger) {
    this.previewTrigger = trigger || this.$('enlarge-preview');
    this.$('large-preview').replaceChildren(this.createPreview(this.active, this.selection[this.active]));
    this.$('enlarged-label').textContent = `${SLOTS[this.active].label} · ${this.dataset.year}`;
    this.$('preview-dialog').showModal();
    this.updateMobileDock();
  }

  canAnimate(element) {
    return typeof element?.animate === 'function' && !globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  }

  fadeIn(element) {
    if (this.canAnimate(element)) element.animate([{ opacity: .25 }, { opacity: 1 }], { duration: 240, easing: 'ease-out' });
  }

  setPresetsOpen(open, onSettled) {
    const panel = this.$('presets-panel');
    const from = panel.hidden ? 0 : panel.getBoundingClientRect().height;
    this.presetsOpen = open;
    this.$('route-presets').setAttribute('aria-expanded', String(open));
    this.$('route-presets').classList.toggle('is-expanded', open);
    this.presetAnimation?.cancel();
    if (!this.canAnimate(panel)) { panel.hidden = !open; onSettled?.(); return; }
    // Keep the panel visible until its closing animation finishes so both
    // directions animate the content and the surrounding layout together.
    panel.hidden = false;
    panel.style.overflow = 'hidden';
    const to = open ? panel.scrollHeight + panel.offsetHeight - panel.clientHeight : 0;
    const animation = panel.animate([{ height: `${from}px`, opacity: open ? 0 : 1 }, { height: `${to}px`, opacity: open ? 1 : 0 }], { duration: 280, easing: 'cubic-bezier(.2,.7,.2,1)', fill: 'both' });
    this.presetAnimation = animation;
    animation.onfinish = () => {
      panel.hidden = !open;
      panel.style.overflow = '';
      animation.cancel();
      this.presetAnimation = null;
      onSettled?.();
    };
  }

  setPanelVisible(panel, visible) {
    if (!this.canAnimate(panel)) { panel.hidden = !visible; return; }
    if (this.reviewTarget === visible || (this.reviewTarget === undefined && panel.hidden === !visible)) return;
    const from = panel.hidden ? 0 : panel.getBoundingClientRect().height;
    this.reviewAnimation?.cancel();
    this.reviewTarget = visible;
    panel.hidden = false;
    panel.style.overflow = 'hidden';
    const animation = panel.animate([
      { height: `${from}px`, opacity: visible ? 0 : 1 },
      { height: `${visible ? panel.scrollHeight + panel.offsetHeight - panel.clientHeight : 0}px`, opacity: visible ? 1 : 0 },
    ], { duration: 260, easing: 'ease-in-out', fill: 'both' });
    this.reviewAnimation = animation;
    animation.onfinish = () => {
      panel.hidden = !visible;
      panel.style.overflow = '';
      animation.cancel();
      this.reviewAnimation = null;
      this.reviewTarget = undefined;
    };
  }

  clearPresetUndo() {
    this.presetUndo = null;
    this.$('undo-preset').hidden = true;
    this.$('preset-status').textContent = '';
  }

  randomPhoto() {
    if (!this.complete || this.busy) return;
    const pool = this.showPlaceNames ? this.catalogue.filter(supportsPlaceNames) : this.catalogue;
    const photo = chooseRandomPhoto(pool, this.selection, this.active);
    if (!photo) return;
    this.rememberPhotoChange();
    this.selection[this.active] = photo; this.wanted[this.active] = photo.id;
    this.clearPresetUndo(); this.design = designId(); this.save(); this.render();
    this.announce(`${photo.title} suggested for ${SLOTS[this.active].label}.`);
    this.showToast(`${SLOTS[this.active].label} selected`);
    this.showPreview();
  }

  renderPresets() {
    this.$('presets-loading').hidden = this.complete;
    if (!this.complete) return;
    this.$('presets').replaceChildren(...PRESETS.map((preset) => {
      const photos = resolvePreset(preset, this.catalogue);
      const available = countSelected(photos) === 14;
      const button = document.createElement('button'); button.type = 'button'; button.className = 'cp-calendar__preset-card'; button.dataset.applyPreset = preset.id; button.disabled = !available;
      button.setAttribute('aria-label', `Use ${preset.title} calendar — 14 editable photographs`);
      const collage = document.createElement('div'); collage.className = 'cp-calendar__preset-photos'; collage.setAttribute('aria-hidden', 'true');
      for (const photo of [photos[0], photos[6], photos[12]].filter(Boolean)) {
        const img = document.createElement('img'); img.src = photo.thumbnail; img.alt = ''; img.width = 480; img.height = 340; img.loading = 'lazy'; collage.append(img);
      }
      const title = document.createElement('strong'); title.textContent = preset.title;
      const description = document.createElement('p'); description.textContent = preset.description;
      button.append(collage, title, description);
      if (!available) { const hint = document.createElement('span'); hint.textContent = 'Currently unavailable'; button.append(hint); }
      return button;
    }));
    this.setPresetsShowingAll(this.presetsShowingAll === true);
  }

  // Larger screens show one row of ready-made calendars first; phones swipe through them all.
  setPresetsShowingAll(showAll) {
    this.presetsShowingAll = showAll;
    const more = this.$('presets-more');
    this.$('presets').classList.toggle('is-limited', !showAll);
    if (!more) return;
    more.hidden = PRESETS.length <= 3;
    more.setAttribute('aria-expanded', String(showAll));
    more.textContent = showAll ? 'Show fewer ready-made calendars' : `Show all ${PRESETS.length} ready-made calendars`;
  }

  applyPreset(id) {
    if (!this.complete || this.busy) return;
    const preset = PRESETS.find((item) => item.id === id);
    if (!preset) return;
    const photos = resolvePreset(preset, this.catalogue);
    if (countSelected(photos) !== 14) { this.announce('Some photographs in this theme are unavailable. Choose another theme or select your own photographs.'); return; }
    this.presetUndo = { wanted: [...this.wanted], design: this.design, active: this.active };
    this.photoUndo = null;
    this.selection = photos; this.wanted = photos.map((photo) => photo.id);
    this.design = designId(); this.active = 1; this.reviewing = true;
    this.save(); this.render();
    this.$('preset-status').textContent = `${preset.title} selected.`;
    this.$('undo-preset').hidden = false;
    this.setPresetsOpen(false, () => {
      this.$('review-panel').scrollIntoView({ block: 'start', behavior: globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
      this.$('review-panel').focus({ preventScroll: true });
    });
    if (!this.announcePendingPlaceNames(`${preset.title} filled all 14 pages. `)) this.announce(`${preset.title} filled all 14 pages.`);
  }

  undoPreset() {
    if (!this.presetUndo || this.busy || !this.complete) return;
    const previous = this.presetUndo;
    this.photoUndo = null;
    this.wanted = previous.wanted; this.selection = restoreSelection(this.wanted, this.catalogue);
    this.design = previous.design; this.active = previous.active; this.reviewing = false;
    this.clearPresetUndo(); this.save(); this.render();
    this.$('preset-status').textContent = 'Previous photos restored.';
    this.announce('Ready-made calendar undone.');
    this.$('current-label').scrollIntoView({ block: 'center', behavior: 'instant' });
    this.$('slots').querySelector(`[data-slot="${this.active}"]`)?.focus({ preventScroll: true });
  }

  renderLocations() {
    const select = this.$('location'); const chosen = select.value;
    const places = new Map();
    this.catalogue.forEach((photo) => photo.locations.forEach((place) => { if (place.handle && place.title) places.set(place.handle, place.title); }));
    select.replaceChildren(new Option('All locations', ''), ...[...places].sort((a, b) => a[1].localeCompare(b[1])).map(([handle, title]) => new Option(title, handle)));
    select.value = chosen;
  }

  renderCatalogue() {
    const scrollTop = this.$('catalogue').scrollTop;
    const focusedPhoto = this.$('catalogue').contains(document.activeElement) ? document.activeElement?.dataset.choosePhoto : null;
    const phrase = normalise(this.$('search').value).trim().replace(/\s+/g, ' ');
    const query = phrase.split(' ').filter(Boolean);
    // A search covers every location; the location menu only applies when nothing is typed.
    const place = query.length ? '' : this.$('location').value;
    const matches = this.catalogue.flatMap((photo) => {
      if (place && !photo.locations.some((location) => location.handle === place)) return [];
      const title = normalise(photo.title);
      const places = normalise(photo.locations.map((location) => location.title).join(' '));
      if (!query.every((word) => title.includes(word) || places.includes(word))) return [];
      // Rank: whole phrase in the title, then every word in the title, then some words, then location-only matches.
      let score = 0;
      if (query.length) {
        if (title.includes(phrase)) score = 4 + (title.startsWith(phrase) ? 1 : 0);
        else if (query.every((word) => title.includes(word))) score = 3;
        else if (query.some((word) => title.includes(word))) score = 2;
        else if (places.includes(phrase)) score = 1;
      }
      return [{ photo, score }];
    }).sort((a, b) => b.score - a.score).map((entry) => entry.photo);
    this.$('results').textContent = `${matches.length} photograph${matches.length === 1 ? '' : 's'}${this.complete ? '' : ' loaded'}${query.length || place ? ' found' : ''}`;
    this.$('clear-filters').hidden = !query.length && !place;
    this.$('empty').hidden = Boolean(matches.length) || this.loading;
    this.$('show-more').hidden = matches.length <= this.visibleLimit;
    this.$('catalogue').replaceChildren(...matches.slice(0, this.visibleLimit).map((photo) => {
      const button = document.createElement('button'); button.type = 'button'; button.className = 'cp-calendar__photo-card'; button.dataset.choosePhoto = photo.id;
      button.setAttribute('aria-label', `Choose ${photo.title} for ${SLOTS[this.active].label}`);
      button.setAttribute('aria-pressed', String(this.selection[this.active]?.id === photo.id));
      const unavailable = this.showPlaceNames && !supportsPlaceNames(photo);
      if (unavailable) { button.setAttribute('aria-disabled', 'true'); button.setAttribute('aria-label', `${photo.title}: place-name version coming soon`); }
      const thumbnail = this.showPlaceNames && photo.namedThumbnail ? photo.namedThumbnail : photo.thumbnail;
      const img = document.createElement('img'); img.src = thumbnail; img.alt = ''; img.loading = 'lazy'; img.width = 480; img.height = 340;
      const text = document.createElement('span'); text.textContent = photo.title;
      if (unavailable) { const note = document.createElement('small'); note.textContent = 'Place-name version coming soon'; text.append(note); }
      const used = this.selection.flatMap((selected, index) => selected?.id === photo.id ? [SLOTS[index].label] : []);
      if (used.length) { const note = document.createElement('small'); note.textContent = used.length > 2 ? `Used on ${used.length} pages` : `Chosen for ${used.join(', ')}`; note.title = used.join(', '); text.append(note); }
      button.append(img, text); return button;
    }));
    this.$('catalogue').scrollTop = scrollTop;
    if (focusedPhoto) this.$('catalogue').querySelector(`[data-choose-photo="${focusedPhoto}"]`)?.focus({ preventScroll: true });
  }

  showReview() {
    this.reviewing = true; this.render();
    this.$('review-panel').focus({ preventScroll: true });
    this.$('review-panel').scrollIntoView({ block: 'start', behavior: 'instant' });
  }

  renderReview() {
    this.$('review-grid').replaceChildren(...SLOTS.map((slot, index) => {
      const photo = this.selection[index]; const button = document.createElement('button'); button.type = 'button'; button.dataset.slot = index; button.className = 'cp-calendar__review-card';
      button.setAttribute('aria-label', `Edit ${slot.label}: ${photo?.title || 'choose a photograph'}`);
      const image = this.createPreview(index, photo ? { ...photo, image: photo.thumbnail, namedImage: photo.namedThumbnail } : null, true);
      const label = document.createElement('span'); label.textContent = slot.label;
      const title = document.createElement('small'); title.textContent = photo?.title || 'Choose a photograph'; label.append(title);
      button.append(image, label); return button;
    }));
  }

  async addToCart() {
    if (this.busy || !this.complete || !this.reviewing || countSelected(this.selection) !== 14 || !this.dataset.variantId) return;
    let properties;
    try { properties = buildProperties(this.selection, { year: this.dataset.year, size: this.dataset.size, designId: this.design, showPlaceNames: this.showPlaceNames === true }); }
    catch (error) { this.announce(error.message); return; }
    this.$('added').hidden = true;
    this.busy = true; this.render(); this.setAttribute('aria-busy', 'true');
    const attemptId = designId();
    this.signalCart('attempted', attemptId);
    this.announce(this.recoveryLine ? 'Updating the calendar in your basket…' : 'Adding your personalised calendar…');
    try {
      const receipt = this.recoveryLine ? await this.finishCartLine(properties) : await this.fetchJSON(this.dataset.addUrl, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: this.dataset.variantId, quantity: 1, properties }] }),
      });
      const added = Array.isArray(receipt?.items) && receipt.items.find((item) => item && String(item.variant_id || item.id) === this.dataset.variantId && item.quantity >= 1 && Object.entries(properties).every(([key, value]) => String(item.properties?.[key]) === String(value)));
      if (!added) throw new Error('We could not confirm the calendar and all its photographs were added.');
      this.signalCart('succeeded', attemptId);
      if (this.recoveryLine) {
        // Finishing a calendar already in the basket: go back to the basket it came from.
        try { localStorage.removeItem(this.storageKey); } catch { /* Optional local draft. */ }
        window.location.assign(this.dataset.cartUrl);
        return;
      }
      // A new calendar: stay here so a second one (a common gift) starts from this design.
      // A fresh design id makes the next calendar its own basket line.
      this.design = designId();
      this.reviewing = false;
      this.busy = false;
      this.removeAttribute('aria-busy');
      this.save();
      this.$('added').hidden = false;
      this.announce('✓ Calendar added to your basket.');
      this.render();
      this.notifyCart();
    } catch (error) {
      this.signalCart('failed', attemptId);
      this.announce(`${error.name === 'AbortError' ? 'The basket update timed out.' : error.message || 'We could not confirm the basket update.'} Check your basket before trying again. Your calendar has been kept.`);
      this.$('check-cart').hidden = false;
      this.busy = false; this.render(); this.removeAttribute('aria-busy');
    }
  }

  /** Updates the header basket count and shows the site's "Added to your basket" toast. */
  async notifyCart() {
    if (typeof CartAddEvent !== 'function' || !globalThis.document) return;
    const cart = await this.fetchJSON(this.dataset.cartJsonUrl || '/cart.js').catch(() => null);
    document.dispatchEvent(new CartAddEvent(cart ?? {}, 'calendar-builder', {
      source: 'calendar-builder', itemCount: Number(cart?.item_count) || 1, addedQuantity: 1,
    }));
  }

  /** After adding: keep this calendar's photos to make another, or clear them for a new one. */
  startAnother(fresh) {
    this.$('added').hidden = true;
    if (fresh) {
      this.selection = emptySelection();
      this.wanted = Array(14).fill(null);
      this.showPlaceNames = false;
      this.presetUndo = null;
      this.photoUndo = null;
    }
    this.design = designId();
    this.active = fresh ? 0 : 1;
    this.reviewing = false;
    this.save();
    this.render();
    this.announce(fresh ? 'New calendar started. Choose your front cover photograph.' : 'Making another calendar from your last one. Change any page you like.');
    this.$('current-label')?.scrollIntoView?.({ block: 'center', behavior: 'instant' });
  }

  signalCart(phase, attemptId) {
    // The shared analytics listener applies consent and emits no photograph names or customer notes.
    try {
      this.dispatchEvent(new CustomEvent('calendar:cart', { bubbles: true, detail: {
        phase, attemptId, operation: this.recoveryLine ? 'complete' : 'add',
        quantity: this.recoveryLine ? this.recoveryQuantity : 1,
        productId: this.dataset.productId, variantId: this.dataset.variantId,
      } }));
    } catch { /* Analytics must not interrupt ordering. */ }
  }

  /** Update only the selected incomplete line; never add a second calendar during recovery. */
  async finishCartLine(properties) {
    const cart = await this.fetchJSON(`${this.dataset.cartUrl}.js`);
    if (!Array.isArray(cart?.items)) throw new Error('Your basket could not be checked.');
    const matches = (item) => item && String(item.variant_id || item.id) === this.dataset.variantId &&
      Object.entries(properties).every(([key, value]) => String(item.properties?.[key]) === String(value));
    const line = cart.items.find((item) => item.key === this.recoveryLine);
    if (!line) {
      // A previous response may have timed out after Shopify changed the line key.
      if (this.recoveryQuantity && cart.items.some((item) => matches(item) && item.quantity === this.recoveryQuantity)) return cart;
      throw new Error('That calendar is no longer in your basket. Return to the basket to continue.');
    }
    if (String(line.variant_id || line.id) !== this.dataset.variantId || hasCalendarPhotos(line.properties)) {
      throw new Error('That basket item has changed. Check your basket before making another calendar.');
    }
    const quantity = Number(line.quantity);
    if (!Number.isInteger(quantity) || quantity < 1) throw new Error('The calendar quantity could not be checked.');
    if (this.recoveryQuantity && this.recoveryQuantity !== quantity) throw new Error('The calendar quantity has changed. Check your basket before continuing.');
    this.recoveryQuantity = quantity;
    this.save();
    // Keep unrelated gift notes; replace only calendar metadata and page labels.
    const extras = Object.fromEntries(Object.entries(line.properties || {}).filter(([key]) =>
      !key.startsWith('_calendar_') && !/^(?:Calendar year|Calendar size|Place names|\d{2} )/.test(key)
    ));
    const updated = await this.fetchJSON(this.dataset.changeUrl, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: line.key, quantity, properties: { ...extras, ...properties } }),
    });
    if (!Array.isArray(updated?.items) || !updated.items.some((item) => matches(item) && item.quantity === quantity)) {
      throw new Error('We could not confirm the calendar was updated.');
    }
    return updated;
  }
}

if (!customElements.get('calendar-builder')) customElements.define('calendar-builder', CalendarBuilder);

// Swipeable photo gallery in the intro, with arrow buttons and a "1 / 4" counter.
class CalendarGallery extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.track = this.querySelector('[data-gallery-track]');
    this.slides = [...this.track.querySelectorAll('.cp-calendar__gallery-slide')];
    this.querySelector('[data-gallery-prev]').addEventListener('click', () => this.step(-1), { signal });
    this.querySelector('[data-gallery-next]').addEventListener('click', () => this.step(1), { signal });
    this.track.addEventListener('scroll', () => { cancelAnimationFrame(this.frame); this.frame = requestAnimationFrame(() => this.update()); }, { signal, passive: true });
    this.track.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') { event.preventDefault(); this.step(1); }
      if (event.key === 'ArrowLeft') { event.preventDefault(); this.step(-1); }
    }, { signal });
    this.update();
  }

  disconnectedCallback() { this.controller?.abort(); cancelAnimationFrame(this.frame); }

  current() {
    const left = this.track.scrollLeft;
    let closest = 0;
    this.slides.forEach((slide, index) => { if (Math.abs(slide.offsetLeft - left) < Math.abs(this.slides[closest].offsetLeft - left)) closest = index; });
    return closest;
  }

  step(direction) {
    const index = Math.min(this.slides.length - 1, Math.max(0, this.current() + direction));
    const reduce = globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    this.track.scrollTo({ left: this.slides[index].offsetLeft, behavior: reduce ? 'instant' : 'smooth' });
  }

  update() {
    const index = this.current();
    this.querySelector('[data-gallery-count]').textContent = `${index + 1} / ${this.slides.length}`;
    this.querySelector('[data-gallery-prev]').disabled = index === 0;
    this.querySelector('[data-gallery-next]').disabled = index === this.slides.length - 1;
  }
}

if (!customElements.get('calendar-gallery')) customElements.define('calendar-gallery', CalendarGallery);
